import { useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Images, MapPin, Phone, Star } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CardVisual } from "@/components/CardVisual";
import { useHref, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

export const ListingCard = ({ listing, index = 0 }) => {
  const t = useT();
  const href = useHref();
  const catLabel = useCategoryLabel();
  const [imgFailed, setImgFailed] = useState(false);
  // Scanned documents are never shown publicly; the category card stands in for them.
  const img = imgFailed ? null : mediaUrl(listing.photos?.[0]);
  const photoCount = (listing.photos || []).length;
  const detailHref = href(`/business/${listing.slug || listing.id}`);
  return (
    <article
      data-testid={`listing-card-${listing.id}`}
      className="rise flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-md"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100">
        <Link to={detailHref} data-testid={`card-image-link-${listing.id}`} className="block h-full w-full">
          {img ? (
            <img
              src={img}
              alt={t("card.cardAlt", { name: listing.business_name })}
              loading="lazy"
              onError={() => setImgFailed(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <CardVisual listing={listing} />
          )}
        </Link>
        {photoCount > 0 && (
          <Link
            to={detailHref}
            data-testid={`photo-count-${listing.id}`}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-slate-900"
          >
            <Images className="h-3.5 w-3.5" />
            {t("card.photos", { count: photoCount })}
          </Link>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <Badge variant="secondary" className="w-fit text-[11px] font-semibold uppercase tracking-wider">
          {catLabel(listing.category)}
        </Badge>
        {listing.is_featured && (
          <Badge
            data-testid={`featured-badge-${listing.id}`}
            className="w-fit bg-amber-100 text-[11px] font-semibold uppercase tracking-wider text-amber-900"
          >
            <Star className="mr-1 h-3 w-3" /> Featured
          </Badge>
        )}
        {listing.verified && (
          <Badge
            data-testid={`verified-badge-${listing.id}`}
            className="w-fit bg-emerald-100 text-[11px] font-semibold uppercase tracking-wider text-emerald-900"
          >
            <BadgeCheck className="mr-1 h-3 w-3" /> {t("card.verified")}
          </Badge>
        )}
        <h3 className="font-display text-lg font-bold leading-snug tracking-tight text-slate-900">
          <Link
            to={detailHref}
            data-testid={`listing-detail-link-${listing.id}`}
            className="transition-colors duration-200 hover:text-primary"
          >
            {listing.business_name}
          </Link>
        </h3>
        <p className="flex items-center gap-1.5 text-sm text-slate-500">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          {listing.district}, {listing.state}
        </p>

        <div className="mt-auto flex flex-col gap-2 pt-2">
          <a
            href={`tel:${listing.phone_number.replace(/\s|-/g, "")}`}
            data-testid={`call-button-${listing.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
          >
            <Phone className="h-4 w-4" /> {listing.phone_number}
          </a>
          <a
            href={listing.whatsapp_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`whatsapp-chat-button-${listing.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-3 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1DA851]"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.06 2.87 1.21 3.07c.15.2 2.09 3.34 5.08 4.55 2.99 1.2 2.99.8 3.53.75.54-.05 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35zM12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20z" />
            </svg>
            {t("card.whatsapp")}
          </a>
        </div>
      </div>
    </article>
  );
};
